import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import {
    add,
    endOfDay,
    endOfMonth,
    endOfWeek,
    endOfYear,
    format,
    isAfter,
    isBefore,
    isSameMonth,
    isSameWeek,
    isSameYear,
    isToday,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
    sub,
} from 'date-fns';
import { AbstractComponentMobile } from '../../abstract/abstract-component-global.directive';
import { DateFormats } from '../../models/date-formats.model';
import { DateInterval } from '../../models/date-interval.model';
import { TimeScale } from '../../models/enums/time-scale.enum';

@Component({
    selector: 'web-timespan',
    templateUrl: './timespan.component.html',
    styleUrls: ['./timespan.component.scss'],
    imports: [CommonModule, IonicModule, TranslateModule],
})
export class TimespanComponent extends AbstractComponentMobile implements OnInit {
    public override tag = '[TimespanComponent]';

    @Input()
    public dateFormats?: DateFormats;

    @Output()
    public newDateEvent = new EventEmitter<DateInterval>();

    public readonly scale = TimeScale;

    public currentScale: TimeScale = this.scale.DAILY;

    public currentDate = new Date();

    public dateInterval?: DateInterval;

    public canGoNext = this.canGoNextDate();
    public canGoPrevious = this.canGoPreviousDate();
    public canUseDayScale = this.dayScaleAvailable();
    public canUseWeekAndMonthScales = this.weekAndMonthScalesAvailables();

    public ngOnInit(): void {
        this.dateInterval = {
            timeScale: this.currentScale,
            displayedDate: this.dateFormats ? format(this.currentDate, this.dateFormats.day) : '',
            startDate: startOfDay(this.currentDate),
            endDate: endOfDay(this.currentDate),
            currentPeriod: true,
        };
        this.newDateEvent.emit(this.dateInterval);
    }

    public changeScale(e: unknown): void {
        if (!(e instanceof CustomEvent)) {
            console.error(`${this.tag} wrong event received`);
            return;
        }
        if (!Object.values(TimeScale).includes(e.detail.value)) {
            console.error(`${this.tag} wrong TimeScale value used`);
            return;
        }

        this.currentScale = e.detail.value;

        /**
         * Rule : when going into YEARL/INFINITY, going back to today
         */
        if (this.currentScale === TimeScale.YEARLY || this.currentScale === TimeScale.INFINITY) {
            this.currentDate = new Date();
        }

        this.updateDate(this.currentDate);
    }

    public next(): void {
        switch (this.currentScale) {
            case this.scale.DAILY:
                this.currentDate = add(this.currentDate, { days: 1 });
                break;
            case this.scale.WEEKLY:
                this.currentDate = add(this.currentDate, { weeks: 1 });
                break;
            case this.scale.MONTHLY:
                this.currentDate = add(this.currentDate, { months: 1 });
                break;
            case this.scale.YEARLY:
                this.currentDate = add(this.currentDate, { years: 1 });
                break;
            default:
                break;
        }
        this.updateDate(this.currentDate);
    }

    public previous(): void {
        switch (this.currentScale) {
            case this.scale.DAILY:
                this.currentDate = sub(this.currentDate, { days: 1 });
                break;
            case this.scale.WEEKLY:
                this.currentDate = sub(this.currentDate, { weeks: 1 });
                break;
            case this.scale.MONTHLY:
                this.currentDate = sub(this.currentDate, { months: 1 });
                break;
            case this.scale.YEARLY:
                this.currentDate = sub(this.currentDate, { years: 1 });
                break;
            default:
                break;
        }
        this.updateDate(this.currentDate);
    }

    public forceUpdate(): void {
        this.currentDate = new Date();
        this.updateDate(this.currentDate);
    }
    /**
     *  Update dateInterval
     */
    private updateDate(date: Date): void {
        // Security measure to prevent going into future
        if (isAfter(this.currentDate, new Date())) {
            this.currentDate = new Date();
        }

        switch (this.currentScale) {
            case TimeScale.DAILY:
                this.dateInterval = {
                    timeScale: this.currentScale,
                    displayedDate: this.dateFormats ? format(date, this.dateFormats.day) : '',
                    startDate: startOfDay(date),
                    endDate: endOfDay(date),
                    currentPeriod: isToday(date),
                };
                break;
            case TimeScale.WEEKLY:
                this.dateInterval = {
                    timeScale: this.currentScale,
                    displayedDate: this.displayWeekString(date),
                    startDate: startOfWeek(date),
                    endDate: endOfWeek(date),
                    currentPeriod: isSameWeek(startOfWeek(date), new Date()),
                };
                break;
            case TimeScale.MONTHLY:
                // If we are from DAILY/WEEKLY at end of month and go on next month, we can have a current date in the future
                // Set it to today
                if (isAfter(date, new Date())) {
                    this.currentDate = new Date();
                }
                this.dateInterval = {
                    timeScale: this.currentScale,
                    displayedDate: this.dateFormats ? format(date, this.dateFormats.month) : '',
                    startDate: startOfMonth(date),
                    endDate: endOfMonth(date),
                    currentPeriod: isSameMonth(startOfMonth(date), new Date()),
                };
                break;
            case TimeScale.YEARLY:
                this.dateInterval = {
                    timeScale: this.currentScale,
                    displayedDate: this.dateFormats ? format(date, this.dateFormats.year) : '',
                    startDate: startOfYear(date),
                    endDate: endOfYear(date),
                    currentPeriod: isSameYear(startOfYear(date), new Date()),
                };
                break;
            case TimeScale.INFINITY:
                // INFINITY datas goes from now to the first appearance of data
                this.dateInterval = {
                    timeScale: this.currentScale,
                    displayedDate: '∞',
                    startDate: startOfYear(date),
                    endDate: endOfYear(new Date()),
                    currentPeriod: isSameYear(startOfYear(date), new Date()),
                };
                break;

            default:
                break;
        }

        this.canGoNext = this.canGoNextDate();
        this.canGoPrevious = this.canGoPreviousDate();
        this.canUseDayScale = this.dayScaleAvailable();
        this.canUseWeekAndMonthScales = this.weekAndMonthScalesAvailables();

        this.newDateEvent.emit(this.dateInterval);
    }

    private displayWeekString(date: Date): string {
        return this.dateFormats
            ? format(startOfWeek(date), this.dateFormats.week) + ' - ' + format(endOfWeek(date), this.dateFormats.week)
            : '';
    }

    /**
     *  Handle disabled state of next button
     */
    public canGoNextDate(): boolean {
        if (
            this.currentScale === TimeScale.INFINITY ||
            isToday(this.currentDate) ||
            isAfter(startOfYear(this.currentDate), new Date())
        ) {
            return false;
        }
        if (
            this.currentScale === TimeScale.MONTHLY &&
            isAfter(add(startOfMonth(this.currentDate), { months: 1 }), new Date()) &&
            !isSameMonth(this.currentDate, sub(new Date(), { months: 1 }))
        ) {
            return false;
        }
        if (
            this.currentScale === TimeScale.WEEKLY &&
            isAfter(add(startOfWeek(this.currentDate), { weeks: 1 }), new Date())
        ) {
            return false;
        }
        return true;
    }

    /**
     *  Handle disabled state of previous button
     */
    public canGoPreviousDate(): boolean {
        if (this.currentScale === TimeScale.INFINITY) {
            return false;
        }

        // Display only current year datas on month an week
        if (this.currentScale === TimeScale.DAILY && isBefore(this.currentDate, sub(new Date(), { months: 1 }))) {
            return false;
        }
        // Display only current year datas on month an week scales
        if (this.currentScale === TimeScale.MONTHLY && isBefore(this.currentDate, sub(new Date(), { years: 1 }))) {
            return false;
        }
        if (this.currentScale === TimeScale.WEEKLY && isBefore(this.currentDate, sub(new Date(), { years: 1 }))) {
            return false;
        }
        if (this.currentScale === TimeScale.YEARLY && isBefore(this.currentDate, sub(new Date(), { years: 1 }))) {
            return false;
        }

        return true;
    }

    /**
     *  Handle disabled state of DAILY button
     */
    public dayScaleAvailable(): boolean {
        if (isBefore(this.currentDate, sub(new Date(), { months: 1 }))) {
            return false;
        }
        return true;
    }

    /**
     *  Handle disabled state of WEEKLY and MONTHLY buttons
     */
    public weekAndMonthScalesAvailables(): boolean {
        if (isBefore(this.currentDate, sub(new Date(), { years: 1 }))) {
            return false;
        }
        return true;
    }
}
